import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ComposableMap,
  ZoomableGroup,
  ProjectionConfig,
} from "react-simple-maps";
import { useMediaQuery } from "@mui/material";
import geoAlbersUsaPr from "./geoAlbersUsaPr";

import GeoMapContainer from "./GeoMapContainer";
import { Markers } from "./Pins/Markers";
import { AppDispatch, RootState } from "../../../../store/store";
import { setPinsForSelectedCountry } from "../../../../features/map/pinsSlice";
import theme from "../../../../components/styles/theme/theme";
import {isRegion} from "../../../../config";
import calculateZoomAndCenter from "./calculateZoomAndCenter";
import { setCenter, setZoom } from "../../../../features/map/mapSlice";

const Map: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    map: { center, selectedCountry },
    pins: { data: dataPins, pinsForSelectedCountry },
  } = useSelector((state: RootState) => state);
  const isXlScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isOverThen1450Resolution = useMediaQuery(theme.breakpoints.up(1450));
  const isOverThen1700Resolution = useMediaQuery(theme.breakpoints.up(1700));

  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const handleResize = () => {
    const containerElement = document.querySelector("#map-container");
    if (containerElement) {
      const { width, height } = containerElement.getBoundingClientRect();
      setContainerWidth(width);
      setContainerHeight(height);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  let zoom = useSelector((state: RootState) => state.map.zoom);
  const selectedState = useSelector((state: RootState) => state.map.selectedState);

  useEffect(() => {
    const pinsForRegion =
      dataPins &&
      selectedCountry &&
      dataPins.find((pins) => pins.name.toUpperCase() === selectedCountry.country_name.toUpperCase());
    pinsForRegion && dispatch(setPinsForSelectedCountry(pinsForRegion.data));
  }, [dataPins, dispatch, selectedCountry]);

  useEffect(() => {
    if (selectedCountry && !selectedState) {
      const updatedZoomAndCenter = calculateZoomAndCenter(
        isXlScreen,
        isOverThen1450Resolution,
        isOverThen1700Resolution,
        containerWidth,
        containerHeight,
        zoom,
        selectedCountry
      );

      dispatch(setCenter(updatedZoomAndCenter.center));
      dispatch(setZoom(updatedZoomAndCenter.zoom));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCountry,
    selectedState,
    isXlScreen,
    isOverThen1450Resolution,
    isOverThen1700Resolution,
    containerWidth,
    containerHeight,
    dispatch,
  ]);

  const usaProjection = useMemo(() => {
    if (!isRegion.usa || selectedState) return undefined;
    return geoAlbersUsaPr().scale(1000).translate([400, 300]);
  }, [selectedState]);

  const projectionConfig: ProjectionConfig = isRegion.usa && !selectedState
    ? { scale: 1000 }
    : isRegion.usa
    ? { scale: 300 }
    : { scale: 300 };

  const handleWheel = (e: React.WheelEvent) => {
    // Prevent scroll-zoom on map — let page scroll normally
    e.stopPropagation();
  };

  const renderMap = () => {
    // USA national view: use AlbersUSA without ZoomableGroup (projection handles layout)
    // USA state view + other regions: use Mercator with ZoomableGroup for zoom/pan
    const useAlbers = isRegion.usa && !selectedState;

    return (
      <div onWheel={handleWheel} style={useAlbers ? { transform: "translateY(-12%)" } : undefined}>
        <ComposableMap
          projection={useAlbers ? usaProjection! : "geoMercator"}
          projectionConfig={useAlbers ? undefined : projectionConfig}
          style={{ width: "100%", height: "100%", background: selectedState ? "transparent" : undefined }}
          className="ComposableMap-container"
        >
          {useAlbers ? (
            <>
              <GeoMapContainer />
              {pinsForSelectedCountry && selectedCountry && (
                <Markers data={pinsForSelectedCountry} />
              )}
            </>
          ) : (
            <ZoomableGroup center={center} zoom={zoom} minZoom={zoom} maxZoom={zoom} filterZoomEvent={() => false}>
              <GeoMapContainer />
              {pinsForSelectedCountry && selectedCountry && (
                <Markers data={pinsForSelectedCountry} />
              )}
            </ZoomableGroup>
          )}
        </ComposableMap>
      </div>
    );
  };

  return (
    <div id="map-container"
      style={{
        paddingLeft: `${isXlScreen && !isRegion.usa ? "98px" : ""}`,
        width: "100%",
        height: "100%",
        ...(isRegion.usa && !selectedState
          ? { aspectRatio: "975 / 610" }
          : { maxHeight: isRegion.usa ? "750px" : "500px", maxWidth: isRegion.usa ? "1000px" : undefined, margin: isRegion.usa ? "0 auto" : undefined, aspectRatio: "4 / 3", overflow: "hidden" }
        ),
      }}
    >
      {renderMap()}
    </div>
  );
};

export default Map;
